import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
  OnDestroy
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { ReuseTabService } from '@delon/abc/reuse-tab';
import { _HttpClient } from '@delon/theme';
import { NzSelectComponent } from 'ng-zorro-antd/select';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { InlineAddPanelComponent } from '../../../inline-add-panel/inline-add-panel.component';
import { TaxCodeAddConfigService } from '../../../inline-add-panel/taxcode-add-config.service';
import { SelectBaseComponent } from '../../select-base';
import { SelectInstantBaseComponent } from '../../selectInstant-base';
import { SelectInstantBaseNewComponent } from '../../selectInstant-base-new';

@Component({
  selector: 'app-input-tax-code-select',
  templateUrl: './input-tax-code-select.component.html',
  styles: [
    `
      /* ===== CONTAINER STYLES ===== */
      .tax-code-select-container {
        position: relative;
        width: 100%;
      }

      /* ===== ADD NEW BUTTON STYLES ===== */
      ::ng-deep .add-new-button {
        color: #000000 !important;
        font-weight: 500 !important;
        width: 100% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 2px !important;
        background-color: transparent !important;
        border: none !important;
      }

      ::ng-deep .add-new-button .anticon {
        color: #000000 !important;
      }

      /* ===== ULTRA COMPACT DROPDOWN STYLES ===== */
      ::ng-deep .ant-select-dropdown:has(.tax-code-select-container) {
        max-height: none !important;
        overflow: hidden !important;
        width: 300px !important;
      }

      ::ng-deep
        .ant-select-dropdown:has(.tax-code-select-container)
        .ant-select-dropdown-menu {
        max-height: 200px !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
      }

      ::ng-deep
        .ant-select-dropdown:has(.tax-code-select-container)
        .ant-select-item-option {
        height: auto !important;
        min-height: 32px !important;
        padding: 4px 12px !important;
      }

      /* ===== SPECIAL OPTION HIGHLIGHTING ===== */
      ::ng-deep .ant-select-item-option-content .special-option {
        text-align: center !important;
      }

      /* ===== YELLOW HIGHLIGHT FOR SECOND ITEM ===== */
      ::ng-deep
        .dropdownSelect:not(:has(.ant-select-item-option-active))
        .ant-select-item-option:nth-child(2):not(
          .ant-select-item-option-active
        ):not(.ant-select-item-option-selected):not(:hover),
      ::ng-deep
        .dropdownSelect
        .ant-select-item-option:first-child:last-child:not(
          .ant-select-item-option-active
        ):not(.ant-select-item-option-selected):not(:hover) {
        background-color: #ffc000;
      }

      /* ===== OVERLAY BACKDROP STYLES ===== */
      ::ng-deep .cdk-overlay-transparent-backdrop {
        background: transparent;
      }

      ::ng-deep .cdk-overlay-pane.inline-add-overlay-panel {
        z-index: 2000 !important;
        pointer-events: auto !important;
      }

      ::ng-deep
        .cdk-overlay-pane.inline-add-overlay-panel.inline-add-overlay-panel--full {
        width: 100vw !important;
        height: 100vh !important;
        max-width: 100vw !important;
        max-height: 100vh !important;
        left: 0 !important;
        top: 0 !important;
      }

      ::ng-deep .cdk-overlay-backdrop.cdk-overlay-transparent-backdrop {
        z-index: 0 !important;
      }

      ::ng-deep .inline-add-overlay-panel {
        pointer-events: all !important;
      }
    `
  ],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputTaxCodeSelectComponent),
      multi: true
    }
  ]
})
export class InputTaxCodeSelectComponent
  extends SelectInstantBaseNewComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('selector') nzSelectComponent!: NzSelectComponent;

  @Input() showAll: boolean = false;
  taxCodeConfig: any;
  inlineDefaults: any = null;
  private overlayRef: OverlayRef | null = null;
  private destroy$ = new Subject<void>();
  private currentInlineAddPanel: InlineAddPanelComponent | null = null;
  private visibilityObserver?: IntersectionObserver;
  private restoreInlineOnVisible: boolean = false;
  private restoreInlineData: any = null;
  // Store instant data separately to preserve it when dropdown closes
  // Holds the value captured at the exact moment user clicks Add
  private pendingInstantName: string = '';

  constructor(
    http: _HttpClient,
    router: Router,
    cdr: ChangeDetectorRef,
    private resueTabService: ReuseTabService,
    private taxCodeAddConfigService: TaxCodeAddConfigService,
    private overlay: Overlay,
    private elementRef: ElementRef
  ) {
    super(http, cdr, router);
  }

  @Input() selectDisabled: boolean = false;

  @Input() tabindex: string | null = null;

  @Input() nzAllowClear: boolean = true;

  @Output() readonly addNewTaxCode = new EventEmitter<any>();

  NotShowInReport: boolean = false;

  ngOnInit(): void {
    this.checkAddNewOptionInReportAccess();
    this.taxCodeConfig = this.taxCodeAddConfigService.getTaxCodeConfig(false);
    // Listen to search form changes to store instant data
    this.searchForm.controls.keyword.valueChanges.subscribe(value => {
      if (value) {
        this.storedInstantData = value.trim();
      }
    });
    // Ensure the correct item is active whenever the list updates while open
    this.onListFilled.subscribe(() => {
      if ((this.nzSelectComponent as any)?.nzOpen) {
        this.ensureFirstItemActivatedAndFocused();
      }
    });
  }

  ngAfterViewInit(): void {
    // Observe component visibility (e.g., when switching tabs) and close overlay when hidden
    try {
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(
          entries => {
            for (const entry of entries) {
              if (!entry.isIntersecting) {
                if (this.overlayRef) {
                  // Capture current form state to restore when tab becomes visible again
                  try {
                    this.restoreInlineData =
                      this.currentInlineAddPanel?.formGroup?.value || null;
                  } catch {
                    this.restoreInlineData = null;
                  }
                  this.restoreInlineOnVisible = true;
                  this.closeOverlay();
                }
              } else if (entry.isIntersecting) {
                if (this.restoreInlineOnVisible) {
                  const dataToRestore = this.restoreInlineData;
                  this.restoreInlineOnVisible = false;
                  this.restoreInlineData = null;
                  // Re-open overlay and restore data
                  this.openOverlay(dataToRestore);
                }
              }
            }
          },
          { root: null, threshold: 0 }
        );
        this.visibilityObserver.observe(this.elementRef.nativeElement);
      }
    } catch {}

    if (this.tabindex) {
      const nzSelectElement =
        this.nzSelectComponent.originElement.nativeElement;
      const searchInput = nzSelectElement.querySelector('input');

      if (searchInput) {
        searchInput.setAttribute('tabindex', this.tabindex?.toString() || '0');

        // Listen to input events directly
        searchInput.addEventListener('input', (event: any) => {
          if (event.target.value) {
            this.storedInstantData = event.target.value.trim();
          }
        });
      }
    }

    const nzSelectElement =
      this.nzSelectComponent?.originElement?.nativeElement;
    if (nzSelectElement) {
      const searchInput = nzSelectElement.querySelector('input');
      if (searchInput) {
        searchInput.addEventListener('input', (event: any) => {
          if (
            event?.target?.value !== undefined &&
            event?.target?.value !== null
          ) {
            this.storedInstantData = String(event.target.value).trim();
          }
        });
      }
    }
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.closeOverlay();
  }

  getUrl(searchValue: string): string {
    let url = this.showAll
      ? 'api/taxcodes/alltax/query?$orderby=code asc&$top=50'
      : 'api/taxcodes/inputtax/query?$orderby=code asc&$top=50';

    if (searchValue) {
      // Check if this is a valueField match request (indicated by special prefix)
      const isValueFieldMatch = searchValue.startsWith(SelectBaseComponent.VALUEFIELD_MATCH_PREFIX);
      const actualSearchValue = isValueFieldMatch
        ? searchValue.substring(SelectBaseComponent.VALUEFIELD_MATCH_PREFIX.length)
        : searchValue;

      const searchStr = actualSearchValue.toString().replaceAll("'", "''");

      // Check if searchValue can be converted to int (ID)
      const searchValueNum = parseInt(actualSearchValue, 10);
      const isNumericId =
        !isNaN(searchValueNum) &&
        searchValueNum.toString() === actualSearchValue.toString().trim();

      if (isValueFieldMatch) {
        // ValueField match: use eq operator for precise matching based on valueField
        // Ignore prerequisite conditions when matching by valueField
        // This ensures we can find records even if they don't meet other conditions
        if (this.valueField === 'id') {
          // Match by ID exactly
          url = `${url}&$filter=(id eq ${isNumericId ? searchValueNum : actualSearchValue})`;
        } else {
          // Match by the specified valueField exactly (code, name, etc.)
          // Use the valueField if set, otherwise default to 'code'
          const fieldName = this.valueField || 'code';
          url = `${url}&$filter=(${fieldName} eq '${encodeURIComponent(searchStr)}')`;
        }
      } else {
        // Partial match: use contains for search behavior
        if (isNumericId) {
          // Include ID matching: (Id eq <id>) or (code contains search)
          url = `${url}&$filter=((id eq ${searchValueNum}) or contains(code, '${encodeURIComponent(searchStr)}'))`;
        } else {
          // Original behavior: only search by code
          url = `${url}&$filter=contains(code, '${encodeURIComponent(searchStr)}')`;
        }
      }
    }
    return url;
  }

  get taxRate() {
    if (this.selected) {
      return this.selected.rate;
    }
    return 0;
  }

  // set the location want to navigate
  jump(): string {
    return '/tax/taxcode/detail/0';
  }

  // set the param
  queryParam(): String {
    return 'TaxCode';
  }

  checkAddNewOptionInReportAccess(): boolean {
    let curUrl = this.resueTabService.curUrl.toLowerCase();

    if (curUrl.includes('report') || curUrl.includes('einv/')) {
      this.NotShowInReport = true;
    } else {
      this.NotShowInReport = false;
    }

    return this.NotShowInReport;
  }

  // Override the addNew method to show inline panel instead of navigation
  override addNew(): void {
    // Ensure latest search value captured before opening panel
    if (!this.storedInstantData) {
      this.captureCurrentSearchValue();
    }
    if (!this.storedInstantData) {
      const currentSearchValue = this.getCurrentSearchValue();
      if (currentSearchValue) {
        this.storedInstantData = currentSearchValue;
      }
    }
    // Load options and defaults first, then show panel
    Promise.all([this.loadSelectOptions(), this.fetchInlineDefaults()]).then(
      () => {
        this.showInlineAddPanel = true;
        // Close the nz-select dropdown explicitly and blur to avoid it reclaiming focus
        if (this.nzSelectComponent) {
          try {
            this.nzSelectComponent.setOpenState(false);
          } catch {}
          try {
            (this.nzSelectComponent as any).blur();
          } catch {}
        }
        this.openOverlay();
      }
    );
  }

  private isSmallScreen(): boolean {
    try {
      return window.matchMedia('(max-width: 768px)').matches;
    } catch {
      return false;
    }
  }

  private openOverlay(restoreData?: any): void {
    if (this.overlayRef) {
      this.closeOverlay();
    }

    const isSmall = this.isSmallScreen();
    const positionStrategy = isSmall
      ? this.overlay.position().global().left('0').top('0')
      : this.overlay
          .position()
          .flexibleConnectedTo(this.elementRef)
          .withPositions([
            {
              originX: 'end',
              originY: 'top',
              overlayX: 'end',
              overlayY: 'top',
              offsetY: 5,
              offsetX: -750
            }
          ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      width: isSmall ? '100vw' : undefined,
      height: isSmall ? '100vh' : undefined,
      scrollStrategy: isSmall
        ? this.overlay.scrollStrategies.block()
        : this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
      disposeOnNavigation: true,
      panelClass: isSmall
        ? ['inline-add-overlay-panel', 'inline-add-overlay-panel--full']
        : ['inline-add-overlay-panel']
    });

    const portal = new ComponentPortal(InlineAddPanelComponent);
    const componentRef = this.overlayRef.attach(portal);
    // Prevent events inside overlay from bubbling to underlying UI which may close it
    const paneEl = (this.overlayRef as any)?._pane as HTMLElement | undefined;
    if (paneEl) {
      const stop = (e: Event) => e.stopPropagation();
      paneEl.addEventListener('mousedown', stop, { passive: true });
      paneEl.addEventListener('click', stop, { passive: true });
      paneEl.addEventListener('mouseup', stop, { passive: true });
    }

    // Configure the inline add panel component
    const inlineAddPanel = componentRef.instance;
    this.currentInlineAddPanel = inlineAddPanel; // Store the reference
    inlineAddPanel.config = this.taxCodeConfig;
    inlineAddPanel.isOverlay = true;
    // Prepare instant data based on search text
    const instant = (
      this.pendingInstantName ||
      this.storedInstantData ||
      this.instantData ||
      ''
    ).trim();
    const initData: any = {
      ...(this.inlineDefaults || {}),
      ...(restoreData || {})
    };
    if (instant) initData.code = instant;
    if (Object.keys(initData).length > 0) inlineAddPanel.initialData = initData;
    inlineAddPanel.visible = true;
    // Ensure lifecycle runs with inputs set
    componentRef.changeDetectorRef.detectChanges();
    // Robustly set the 'code' control once the form is ready
    const tryApplyInstantToForm = (retries: number = 20) => {
      if (!instant) return;
      const fg: any = (inlineAddPanel as any).formGroup;
      // Prefer configured instant field; fallback to 'code'
      const instantField =
        (inlineAddPanel as any).config?.fields?.find(
          (f: any) => f.isInstantData
        )?.name || 'code';
      const control = fg?.get ? fg.get(instantField) : null;
      if (control) {
        control.setValue(instant);
        control.markAsTouched();
        control.markAsDirty();
        // Notify field change to trigger any dependent logic
        try {
          (inlineAddPanel as any).onFieldChange(instantField, instant);
        } catch {}
        // Refresh view
        try {
          componentRef.changeDetectorRef.detectChanges();
        } catch {}
        // Clear pending since we've applied it
        this.pendingInstantName = '';
        return;
      }
      if (retries > 0) {
        setTimeout(() => tryApplyInstantToForm(retries - 1), 50);
      }
    };
    tryApplyInstantToForm();

    // Subscribe to events
    inlineAddPanel.itemCreated
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        this.onTaxCodeCreated(event);
        this.closeOverlay();
      });

    inlineAddPanel.cancelled.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.onTaxCodeCancelled();
      this.closeOverlay();
    });

    inlineAddPanel.visibleChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(visible => {
        if (visible) {
          const instantNow = (
            this.storedInstantData ||
            this.instantData ||
            ''
          ).trim();
          if (instantNow) {
            try {
              inlineAddPanel.changeInstantData(instantNow);
            } catch {}
            setTimeout(() => {
              try {
                inlineAddPanel.changeInstantData(instantNow);
              } catch {}
            }, 0);
          }
        } else {
          this.closeOverlay();
        }
      });

    inlineAddPanel.moreDetails.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.onTaxCodeMoreDetails();
      this.closeOverlay();
    });

    inlineAddPanel.fieldChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        this.onFieldChange(event);
      });

    // Removed backdrop close; rely on inline panel events to close
  }

  private closeOverlay(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.currentInlineAddPanel = null; // Clear the reference
    this.showInlineAddPanel = false;
  }

  onTaxCodeSaved(taxCodeData: any): void {
    // Create the tax code object in the expected format
    const taxCode = {
      id: taxCodeData.id,
      code: taxCodeData.code,
      description: taxCodeData.description,
      rate: taxCodeData.rate,
      eInvoiceTaxType: taxCodeData.eInvoiceTaxType
    };

    // Add the new tax code to the list and select it
    this.list.unshift(taxCode);

    // Set the tax code object in the form control to trigger value changes
    this.value = taxCode.id;
    this.selected = taxCode.id;

    // Set the tax code object directly in the form control
    this.onChange(taxCode);
    this.selectedChange.emit(taxCode);
    this.showInlineAddPanel = false;
  }

  onTaxCodeCreated(event: { item: any; selectionConfig?: any }): void {
    if (event.item && event.selectionConfig) {
      // Create the tax code object in the expected format
      const taxCode = {
        id: event.item[event.selectionConfig.valueField],
        code: event.item.code,
        description: event.item.description,
        rate: event.item.rate,
        eInvoiceTaxType: event.item.eInvoiceTaxType
      };

      // Add the new tax code to the list and select it
      this.list.unshift(taxCode);

      // Close the inline panel first
      this.showInlineAddPanel = false;

      // Set the tax code object in the form control to trigger value changes
      this.value = taxCode.id;
      this.selected = taxCode.id;

      // Set the tax code object directly in the form control
      this.onChange(taxCode);
      this.writeValue(taxCode);
      // Emit addNewTaxCode event for parent component to handle
      this.addNewTaxCode.emit(taxCode);

      // Clear stored instant data after successful creation
      this.clearStoredInstantData();

      // Force change detection
      this.cdr.detectChanges();
    }
  }

  onTaxCodeCancelled(): void {
    this.showInlineAddPanel = false;
    this.clearStoredInstantData();
  }

  onTaxCodeMoreDetails(): void {
    // Navigate to tax code details page with all the current form data
    const formData = this.currentInlineAddPanel?.formGroup?.value;
    if (formData) {
      // Create query params with all form fields
      const queryParams: any = {
        autoPopulate: 1
      };

      // Add all form fields to query params
      if (formData.code) queryParams.TaxCode = formData.code;
      if (formData.description) queryParams.Description = formData.description;
      if (formData.rate) queryParams.Rate = formData.rate;
      if (formData.eInvoiceTaxType)
        queryParams.EInvoiceTaxType = formData.eInvoiceTaxType;

      queryParams.autoPopulate = 1;

      this.router.navigate(['/tax/taxcode/detail/0'], {
        queryParams: queryParams
      });
    } else {
      this.router.navigate(['/tax/taxcode/detail/0'], {
        queryParams: {
          autoPopulate: 1
        }
      });
    }
    this.showInlineAddPanel = false;
    this.clearStoredInstantData();
  }

  onFieldChange(event: { fieldName: string; value: any }): void {
    // Handle field changes - auto-code component will handle code generation
  }

  private loadSelectOptions(): Promise<void> {
    // For tax codes, we don't need to load additional options like customers do
    return Promise.resolve();
  }

  private fetchInlineDefaults(): Promise<void> {
    // Call server to get defaults similar to taxcode/detail new()
    return new Promise(resolve => {
      this.http.get('api/taxcodes/new').subscribe({
        next: (data: any) => {
          const defaults: any = {};
          if (data?.rate) defaults.rate = data.rate;
          if (data?.eInvoiceTaxType)
            defaults.eInvoiceTaxType = data.eInvoiceTaxType;
          this.inlineDefaults = defaults;
          resolve();
        },
        error: () => {
          this.inlineDefaults = null;
          resolve();
        }
      });
    });
  }

  override async writeValue(value: any) {
    // Extract ID if value is an object (e.g., from API response)
    let idValue = value;
    if (value && typeof value === 'object' && this.valueField) {
      idValue = value[this.valueField] || value.id;
    }

    this.value = idValue;

    // If use selected item as value
    if (this.value) {
      if (!this.valueField) {
        this.ensureItemPresentList(value);
        this.selected = value;
      } else {
        // When valueField is set, we need to fetch the list to find the matching item
        // Use valueField match to find the record that exactly matches the valueField (ID or Code)
        // Prefix keyword with special marker to indicate valueField match (ignores prerequisite conditions)
        let keyword = `${SelectBaseComponent.VALUEFIELD_MATCH_PREFIX}${idValue}`;
        await this.fetchData(keyword);
        this.selected = this.list.find(x => x[this.valueField] == idValue);
      }
    } else {
      this.selected = null;
      //this.list = [];
    }
  }
}
